<template>
  <v-autocomplete
    :value="value"
    :items="items"
    item-text="text"
    item-value="id"
    :filter="filterParty"
    :search-input.sync="search"
    :label="label"
    :hint="$t('finance.reports2.accountStatement.partyPickerHint')"
    :no-data-text="$t('finance.reports2.accountStatement.partyNoResults')"
    :loading="loading"
    clearable
    hide-details
    class="fin-field fin-field--wide"
    @change="onChange"
  >
    <template slot="item" slot-scope="data">
      <v-list-tile-content>
        <v-list-tile-title>
          {{ data.item.name }}
          <span class="asp-muted">#{{ data.item.id }}</span>
          <span v-if="data.item.is_deleted" class="asp-muted">({{ $t("finance.reports2.accountStatement.partyDeleted") }})</span>
        </v-list-tile-title>
        <v-list-tile-sub-title>
          <span v-if="data.item.extra">{{ data.item.extra }}</span>
          <span v-if="data.item.account_codes.length" class="asp-codes">{{ data.item.account_codes.join(" / ") }}</span>
          <span v-else class="asp-muted">{{ $t("finance.reports2.accountStatement.partyNoAccount") }}</span>
        </v-list-tile-sub-title>
      </v-list-tile-content>
    </template>
    <template v-if="hasMore" slot="append-item">
      <v-list-tile class="asp-more">
        <v-list-tile-content>
          <v-list-tile-sub-title class="asp-more-text">{{ $t("finance.reports2.accountStatement.partyMore", { limit: limit }) }}</v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
    </template>
  </v-autocomplete>
</template>

<script>
/*
 * Party picker of the account statement (report 2.5, party mode).
 *
 * Reads the statement endpoint's own lookup (mode=parties), which answers
 * under VIEW_FINANCE_REPORTS: a finance reader needs none of the operational
 * list permissions (DRIVERS / TRANSFER_CENTERS / BUSINESS_ACCOUNTS /
 * CAR_OWNERS). EVERY party type is searched on the server (name, phone with
 * or without a prefix, id, ledger code), debounced: the lookup answers at
 * most `limit` rows, so filtering a first page locally would make every
 * later party unreachable. When the server says the cap was hit (has_more),
 * the list ends with a line asking for a narrower search. The selected
 * party is kept in the item list so the selection never disappears while
 * the next search is in flight.
 *
 *   <account-statement-party-picker v-model="partyId" party-type="captain" @change="party => ..." />
 */
export default {
  name: "AccountStatementPartyPicker",
  props: {
    value: { type: [Number, String], default: null },
    partyType: { type: String, required: true },
    label: { type: String, default: "" },
  },
  data() {
    return {
      parties: [],
      hasMore: false,
      limit: 50,
      selected: null,
      search: null,
      loading: false,
      timer: null,
      requestSeq: 0,
    };
  },
  computed: {
    items() {
      const list = this.parties.slice();
      if (this.selected && !list.some((p) => String(p.id) === String(this.selected.id))) {
        list.unshift(this.selected);
      }
      return list.map((party) =>
        Object.assign({}, party, {
          text: party.name + " #" + party.id + (party.extra ? " " + party.extra : ""),
        })
      );
    },
  },
  watch: {
    partyType() {
      this.parties = [];
      this.hasMore = false;
      this.selected = null;
      this.load("");
    },
    search(value) {
      const text = value || "";
      if (this.selected && text === this.itemText(this.selected)) {
        return; // Vuetify echoes the selected item's text into the search box
      }
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.load(text), 350);
    },
  },
  mounted() {
    this.load("");
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
  methods: {
    itemText(party) {
      return party.name + " #" + party.id + (party.extra ? " " + party.extra : "");
    },
    load(search) {
      const seq = ++this.requestSeq;
      const params = { mode: "parties", party_type: this.partyType };
      if (search) {
        params.search = search;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_ACCOUNT_STATEMENT, { params: params })
        .then((response) => {
          if (seq !== this.requestSeq) {
            return;
          }
          const data = (response.data && response.data.data) || {};
          this.parties = data.parties || [];
          this.hasMore = !!data.has_more;
          if (data.limit) {
            this.limit = data.limit;
          }
          if (this.value && !this.selected) {
            this.selected = this.parties.find((p) => String(p.id) === String(this.value)) || null;
          }
        })
        .catch((error) => {
          if (seq === this.requestSeq) {
            window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error), "red");
          }
        })
        .finally(() => {
          if (seq === this.requestSeq) {
            this.loading = false;
          }
        });
    },
    frErrorMessage(error) {
      const data = error && error.response && error.response.data;
      return (data && data.message) || this.$t("finance.reports2.accountStatement.partyFailedLoad");
    },
    filterParty() {
      return true; // the server already matched (every party type)
    },
    onChange(id) {
      const value = id === undefined || id === "" ? null : id;
      this.selected = value === null ? null : this.items.find((p) => String(p.id) === String(value)) || null;
      this.$emit("input", value);
      this.$emit("change", this.selected);
    },
  },
};
</script>

<style scoped>
.asp-muted {
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  margin-inline-start: 4px;
}
.asp-more-text {
  white-space: normal;
  color: #e65100;
}
.asp-codes {
  direction: ltr;
  unicode-bidi: embed;
  margin-inline-start: 6px;
  font-size: 12px;
}
</style>
